import { getGeneric, postGeneric, putGeneric } from '../../common/constants'

// Decision-layer config CRUD — calls the proship Java API (/api/allocation/config/**),
// which writes append-only to proship_client_pace; PACE reads it for allocation.
const BASE = 'allocation/config'

export const GLOBAL_SCOPE = 'global'
export const merchantScope = (merchantId) => `merchant#${merchantId}`
// PACE+ #3: COD / Prepaid knob sets live in their own scopes — `global:COD`,
// `merchant#<id>:PREPAID`. PACE resolves global → global:<pm> → category → merchant →
// merchant:<pm>, most specific wins; an unset payment scope falls through.
export const withPaymentMode = (scope, paymentMode) => (paymentMode ? `${scope}:${paymentMode}` : scope)

// PACE+ #4: named knob presets for comparing PACE output / demos. Mirrors PACE
// `PRESET_CATALOG` (served on /pace/v1/admin/knob-catalog). Compare-only — offered on
// re-score and External Shadow Run, never applied to live allocation.
export const PACE_PRESETS = [
  { key: 'delivery_optimizer', label: 'Delivery % optimiser',
    description: 'Picks the courier most likely to deliver (97% weight on Delivery %).' },
  { key: 'fata_optimizer', label: 'FATA optimiser',
    description: 'Picks the courier most likely to make the first attempt within the promised EDD (97% on FATA).' },
  { key: 'cost_optimizer', label: 'Cost optimiser',
    description: 'Picks the lowest-cost courier: 97% on the logistics bucket, speed↔cost blend fully on cost (α = 0).' },
  { key: 'speed_optimizer', label: 'Speed optimiser',
    description: 'Picks the quickest courier: 97% on the logistics bucket, speed↔cost blend fully on speed (α = 1).' },
  { key: 'even_blend', label: 'Even speed–cost blend (α = 0.5)',
    description: "The merchant's own quality weights, with the speed↔cost blend at 50/50 (α = 0.5)." },
]
export const presetLabel = (key) => (PACE_PRESETS.find((p) => p.key === key) || {}).label || key

// ── API calls ──────────────────────────────────────────────────────────────
export const fetchKnobs = (scope) => getGeneric(`${BASE}/knobs`, { scope })
// What a merchant inherits with no override: its payment-risk category + that category's
// values (only PACE knows the category), via the proship relay. Read-only.
export const fetchMerchantKnobDefaults = (merchantId) =>
  getGeneric('allocation/knob-defaults', { merchantId })
// category#COD_HEAVY_RISKY → "COD-heavy (risky)" for display
export const categoryLabel = (risk) => ({
  COD_HEAVY_RISKY: 'COD-heavy (risky)', COD_HEAVY_HEALTHY: 'COD-heavy (healthy)',
  PREPAID_LED: 'Prepaid-led', MIXED: 'Mixed', UNCLASSIFIED: 'Unclassified',
}[risk] || risk)
export const fetchKnobHistory = (scope, key) =>
  getGeneric(`${BASE}/knobs/history`, { scope, key })
export const saveKnob = (scope, key, value, reason) =>
  putGeneric(`${BASE}/knobs`, { scope, key, value, reason })

// Per-merchant PACE live switch (allocationEngineVersion "V2" = live; otherwise the rules decide). Persists to
// MerchantConfig.allocationEngineVersion via proship's generic field updater
// (ProshipAdmin-gated) — the same field the rule engine reads. {id} = merchant OID.
export const fetchMerchantConfig = (merchantOid) => getGeneric(`config/merchant/${merchantOid}`)
export const setMerchantEngine = (merchantOid, version) =>
  postGeneric(`config/merchant/${merchantOid}/updatefield`, {
    field: 'allocationEngineVersion', value: version,
  })
export const setMerchantShadow = (merchantOid, enabled) =>
  postGeneric(`config/merchant/${merchantOid}/updatefield`, {
    field: 'paceV2ShadowEnabled', value: enabled,
  })
// Rules-vs-PACE shadow agreement (proship PaceShadowController). Omit merchantId for the global view.
// mode=LIVE returns a PACE-only summary (no agreement/deltas); SHADOW/absent returns agreement view.
export const fetchShadowSummary = (days = 7, merchantId, mode) => {
  const params = { days }
  if (merchantId) params.merchantId = merchantId
  if (mode) params.mode = mode
  return getGeneric('allocation/shadow/summary', params)
}
// Recent PACE result rows (shadow + live), newest first. Server-paginated:
// returns { content, page, size, total }. Optional mode (LIVE|SHADOW) / reference filters.
// A reference lookup ignores the time window (targets one shipment across all time).
export const fetchShadowRecords = (days = 7, page = 0, size = 50, merchantId, mode, reference) => {
  const params = { days, page, size }
  if (merchantId) params.merchantId = merchantId
  if (mode) params.mode = mode
  if (reference) params.reference = reference
  return getGeneric('allocation/shadow/records', params)
}

// Config-only re-score of a stored allocation under the merchant's CURRENT knobs
// (proship PaceRescoreController → PACE /pace/v1/allocate/rescore). Non-persistent —
// lets ops see how a config change re-ranks a past shipment without new traffic.
// payload = { merchant_id, service_type?, candidates: [stored v2_ranked_json rows] }.
export const rescoreAllocation = (payload) => postGeneric('allocation/rescore', payload)

// PRISM iEDD (read-only, via proship /api/prism proxy → PRISM :8098)
export const fetchPrismShadowSummary = (days = 7, merchantId) =>
  getGeneric('prism/shadow/summary', merchantId ? { days, merchantId } : { days })
export const fetchPrismIedd = (awb) =>
  getGeneric(`prism/iedd/${encodeURIComponent(awb)}`)
// Paginated per-shipment iEDD-vs-shown-vs-actual rows. Returns { content, page, size, total }.
// Each row carries iedd_ladder {p50,p90,p95,p98} plus ladder_hit / ladder_days_off.
// sort/order are applied server-side over the WHOLE result set (PRISM whitelists
// the column, so an unknown one falls back rather than erroring); breachedOnly
// keeps only shipments that missed the displayed promise.
export const fetchPrismShadowRecords = (
  days = 7, page = 0, size = 50, merchantId, status, tier, opts = {},
) => {
  const params = { days, page, size }
  if (merchantId) params.merchantId = merchantId
  if (status) params.status = status
  if (tier) params.tier = tier
  if (opts.sort) { params.sort = opts.sort; params.order = opts.order || 'desc' }
  if (opts.breachedOnly) params.breachedOnly = true
  return getGeneric('prism/shadow/records', params)
}

// ── Catalogs (mirror PACE GET /pace/v1/admin/knob-catalog — the source of truth).
// TODO: fetch live from PACE so this never drifts; bundled to avoid a 2nd base URL/CORS.
export const KNOB_CATALOG = [
  { key: 'rto_cost', label: 'RTO cost', group: 'cost', unit: 'INR', default: 180.0, min: 0, max: null,
    description: 'Cost charged per RTO. Priced as rto_cost × P(RTO).' },
  { key: 'lost_cost', label: 'Lost/damaged cost', group: 'cost', unit: 'INR', default: 1200.0, min: 0, max: null,
    description: 'Cost of a lost/damaged parcel. Priced as lost_cost × lost_rate.' },
  { key: 'reattempt_cost', label: 'Reattempt cost', group: 'cost', unit: 'INR', default: 45.0, min: 0, max: null,
    description: 'Cost of a failed first attempt. Priced as reattempt_cost × (1 − P(first attempt OK)).' },
  { key: 'surcharge_weight', label: 'Surcharge weight', group: 'cost', unit: null, default: 1.0, min: 0, max: null,
    description: 'Multiplier on the expected weight-discrepancy surcharge.' },
  { key: 'risk_weight', label: 'Risk-cost weight', group: 'cost', unit: null, default: 0.0, min: 0, max: null,
    description: 'Fraction of the priced risk terms (RTO + reattempt + loss) folded into cost. '
      + '0 = cost is real money only (quote + surcharge); 1 = full risk pricing.' },
  { key: 'alpha', label: 'Speed↔cost blend (α)', group: 'logistics', unit: null, default: 0.5, min: 0, max: 1,
    description: 'How the logistics bucket trades speed against cost: 0 = cost only, 1 = speed only.' },
  { key: 'lambda_fast', label: 'Speed normaliser (λ_fast)', group: 'logistics', unit: null, default: 0.0138889, min: 0, max: null,
    description: 'Brings transit hours onto the speed/cost blend scale (≈ 1 / typical hours).' },
  { key: 'lambda_cheap', label: 'Cost normaliser (λ_cheap)', group: 'logistics', unit: null, default: 0.0111111, min: 0, max: null,
    description: 'Brings rupees onto the speed/cost blend scale (≈ 1 / typical cost).' },
  { key: 'min_delivery_prob', label: 'Min delivery probability', group: 'guardrail', unit: 'probability', default: 0.5, min: 0, max: 1,
    description: 'Drop couriers below this P(delivered) before ranking.' },
  { key: 'lost_rate_default', label: 'Default lost rate', group: 'guardrail', unit: 'probability', default: 0.002, min: 0, max: 1,
    description: 'Fallback P(lost) until a per-courier empirical rate exists.' },

  // ── PRISM iEDD knobs (prism_ prefixed) — same decision_param store, global or per-merchant.
  //    PACE ignores prism_* keys and vice-versa. Tune these to calibrate the iEDD promise.
  { key: 'prism_display_quantile', label: 'iEDD display quantile', group: 'prism-iedd', unit: 'quantile', default: 0.98, min: 0.5, max: 0.98,
    // Discrete: PRISM only fits heads at these five rungs and snaps anything else
    // to the nearest one, so a free-text number would misreport what was saved.
    options: [
      { value: 0.5, label: 'P50 — median (ops only, uncalibrated)' },
      { value: 0.8, label: 'P80 — 83% on-time' },
      { value: 0.9, label: 'P90 — 93% on-time' },
      { value: 0.95, label: 'P95 — 97% on-time (recommended)' },
      { value: 0.98, label: 'P98 — 99% on-time (default, widest)' },
    ],
    description: 'Which rung of the promise ladder the buyer sees. Backtest (976k delivered): P50 3.2d/49.7% · P80 4.7d/82.9% · P90 6.2d/92.9% · P95 7.8d/97.0% · P98 10.7d/99.1%. Amazon requires 90% on-time seller-fulfilled (95% recommended) and 93.5% for Seller Fulfilled Prime — P95 is the tightest rung clearing every floor. P50 is a raw median with no calibration: ops signal, never a buyer promise. SLA accounting (iedd_p98) is unaffected by this knob.' },
  { key: 'prism_fallback_buffer_air_h', label: 'iEDD fallback buffer — AIR', group: 'prism-iedd', unit: 'hours', default: 12, min: 0, max: null,
    description: 'Hours added to courier rate-card TAT for a no-history AIR courier (fallback attribution).' },
  { key: 'prism_fallback_buffer_surface_h', label: 'iEDD fallback buffer — SURFACE', group: 'prism-iedd', unit: 'hours', default: 24, min: 0, max: null,
    description: 'Hours added to courier rate-card TAT for a no-history SURFACE courier.' },
  { key: 'prism_cascade_high_n', label: 'iEDD cascade — HIGH n', group: 'prism-iedd', unit: 'records', default: 100, min: 0, max: null,
    description: '90-day courier×dest-state records needed for courier_code (HIGH) attribution.' },
  { key: 'prism_cascade_med_n', label: 'iEDD cascade — MED n', group: 'prism-iedd', unit: 'records', default: 30, min: 0, max: null,
    description: '90-day records for mother_service (MEDIUM) attribution; below this falls to service_type.' },
  { key: 'prism_spread_high_h', label: 'iEDD spread — HIGH max', group: 'prism-iedd', unit: 'hours', default: 120, min: 0, max: null,
    description: 'Max P98−P10 spread (h) for a single-date HIGH promise; wider ⇒ shown as a date range.' },
  { key: 'prism_spread_med_h', label: 'iEDD spread — MED max', group: 'prism-iedd', unit: 'hours', default: 200, min: 0, max: null,
    description: 'Max spread (h) for MEDIUM; wider ⇒ LOW date range.' },
  { key: 'prism_ndr_success_threshold', label: 'iEDD NDR success threshold', group: 'prism-iedd', unit: 'probability', default: 0.60, min: 0, max: 1,
    description: 'Below this P(next attempt succeeds), show a date range instead of a single date.' },
  // ── quality weights + empirical scorecard (recency-weighted) ──
  { key: 'w_delivery', label: 'Weight: Delivery %', group: 'scorecard', unit: null, default: 0.40, min: 0, max: null,
    description: 'Relative importance of empirical delivery % (Wilson LB).' },
  { key: 'w_fasr', label: 'Weight: FASR %', group: 'scorecard', unit: null, default: 0.20, min: 0, max: null,
    description: 'Relative importance of first-attempt strike rate (delivered on 1st attempt, timing-agnostic).' },
  { key: 'w_tat', label: 'Weight: First-attempt TAT adherence', group: 'scorecard', unit: null, default: 0.20, min: 0, max: null,
    description: 'Relative importance of the first attempt landing within promised EDD (discipline).' },
  { key: 'w_cost', label: 'Weight: Logistics (cost + speed)', group: 'scorecard', unit: null, default: 0.20, min: 0, max: null,
    description: 'Importance of the logistics bucket — speed AND cost, blended by α (this is w_logi).' },
  { key: 'recency_mult_7d', label: 'Recency ×: last 7 days', group: 'scorecard', unit: null, default: 1.5, min: 0, max: null,
    description: 'Multiplier on shipments in the ≤7-day bucket when weighting scorecard KPIs.' },
  { key: 'recency_mult_30d', label: 'Recency ×: 8–30 days', group: 'scorecard', unit: null, default: 1.25, min: 0, max: null,
    description: 'Multiplier on shipments in the 8–30-day bucket.' },
  { key: 'recency_mult_90d', label: 'Recency ×: 31–90 days', group: 'scorecard', unit: null, default: 1.0, min: 0, max: null,
    description: 'Multiplier on shipments in the 31–90-day bucket.' },
  { key: 'scorecard_support_min', label: 'Min cascade support', group: 'scorecard', unit: 'shipments', default: 30.0, min: 1, max: null,
    description: "Min recency-weighted shipments for a cascade rung (courier×cohort-lane → drop-cohort → drop-state → courier) to be used. Only when 'Scorecard: shrink small samples' is 0." },
  { key: 'scorecard_shrinkage', label: 'Scorecard: shrink small samples', group: 'scorecard', unit: null, default: 1.0, min: 0, max: 1,
    options: [
      { value: 1, label: '1 — on (default): thin samples pulled toward the courier\'s broader record' },
      { value: 0, label: '0 — legacy: first level with min support, Wilson lower bound' },
    ],
    description: "1 = each courier's rate on a lane is pulled toward its broader record (drop cohort → state → courier → network) by 'Scorecard: prior strength' shipments, and every courier is scored at the finest level it has. 0 = legacy: first level with ≥ min support, scored at its Wilson 95% lower bound (penalises small samples)." },
  { key: 'scorecard_prior_n', label: 'Scorecard: prior strength', group: 'scorecard', unit: 'shipments', default: 30.0, min: 1, max: null,
    description: 'With shrinkage on: how many pseudo-shipments of the broader record are blended into each level. 30 = a lane with 30 shipments is half its own record, half the broader one.' },
  { key: 'ontime_vs_lane', label: 'On-time: one yardstick per lane', group: 'scorecard', unit: null, default: 1.0, min: 0, max: 1,
    options: [
      { value: 1, label: '1 — on (default): same on-time bar for every courier on the lane' },
      { value: 0, label: '0 — legacy: on-time vs each courier\'s own EDD' },
    ],
    description: "1 = on-time = first attempt within the lane's P75 pickup→first-attempt time across ALL couriers (same bar for everyone). 0 = legacy: first attempt by the order's EDD, which comes from each courier's own promised TAT (rewards loose promises)." },
  { key: 'cold_start_peer_median', label: 'No-history courier: peer median', group: 'scorecard', unit: null, default: 1.0, min: 0, max: 1,
    options: [
      { value: 1, label: '1 — on (default): median of the other couriers on the order' },
      { value: 0, label: '0 — legacy: ML-model estimates' },
    ],
    description: '1 = a courier with no scorecard history gets the median delivery / first-attempt / on-time of the other couriers on the same order (same service type first). 0 = legacy: ML-model estimates (first-attempt estimate also used as on-time; very low for new couriers).' },
  { key: 'cold_start_min_history', label: 'No-history courier: min shipments', group: 'scorecard', unit: 'shipments', default: 30.0, min: 0, max: null,
    description: "With shrinkage on: a courier with fewer recency-weighted shipments than this in total (all lanes) is treated as having no history — it gets the peer median (or the ML estimates when 'No-history courier: peer median' is 0). Without this, 1–5 shipments ever would score close to the all-India average. 0 = off." },
  // ── unified scoring (the only engine) — the weights select the behaviour; see PACE docs/07 ──
  { key: 'logistics_cap', label: 'Logistics cap', group: 'unified', unit: null, default: 2.0, min: 0, max: null,
    description: 'Caps the λ-scaled speed+cost penalty so the logistics bucket can’t swamp the delivery/discipline weights on expensive or slow lanes.' },
  // ── where defaults come from (PACE use_category_defaults) ──
  { key: 'use_category_defaults', label: 'Use category defaults', group: 'defaults', unit: null, default: 0.0, min: 0, max: 1,
    options: [
      { value: 0, label: '0 — off (default): global → COD/Prepaid → merchant only' },
      { value: 1, label: '1 — on: COD-heavy / Prepaid-led merchants take their category defaults' },
    ],
    description: "Off by default: a knob with no COD / Prepaid value uses the global value here, never a payment-risk category default. Set 1 (globally, or on one merchant) to let COD-heavy-risky / Prepaid-led merchants take their category's default weights over global." },
  // ── KCI disruption events (PACE decision/kci.py) — proship sends the KCI events matching each
  //    candidate's courier + pickup/drop area; these decide whether/how much they push the ETA.
  { key: 'kci_enabled', label: 'KCI events: apply to ETA', group: 'kci', unit: null, default: 0.0, min: 0, max: 1,
    options: [
      { value: 0, label: '0 — shadow only (shown in the explainer, ETA unchanged)' },
      { value: 1, label: '1 — live KCI disruptions push the courier ETA' },
    ],
    description: 'A live KCI event on a courier\'s pickup area (at pickup) or drop area (at the estimated delivery) adds its EDD extension to that courier\'s ETA, so a disrupted courier loses speed score. 0 = computed and shown only.' },
  { key: 'kci_backlog_grace_h', label: 'KCI backlog grace', group: 'kci', unit: 'hours', default: 24.0, min: 0, max: null,
    description: 'Hours after a KCI event ends during which a pickup/delivery still counts as disrupted (courier backlog clearance).' },
  { key: 'kci_default_delay_h', label: 'KCI default delay', group: 'kci', unit: 'hours', default: 24.0, min: 0, max: null,
    description: 'ETA push for a matched KCI event that carries no EDD extension or revised EDD of its own.' },
]

// Business Knobs UI tiers (Frequently Adjusted vs Advanced) for the PACE page.
// Kept in the frontend, not derived from KNOB_CATALOG, since the catalog is slated to be
// fetched live from PACE (see TODO above) and the tier split is an admin-UX decision.
// Advanced = normalisers / calibration internals that are rarely tuned and easy to mis-set.
export const ADVANCED_KNOB_KEYS = new Set([
  // risk pricing: off by default (risk_weight 0) until the costs are calibrated, so the
  // three priced costs + the gate are rarely touched and easy to mis-set
  'rto_cost',
  'lost_cost',
  'reattempt_cost',
  'risk_weight',
  'surcharge_weight',
  'lambda_fast',
  'lambda_cheap',
  'lost_rate_default',
  'recency_mult_7d',
  'recency_mult_30d',
  'recency_mult_90d',
  'scorecard_support_min',
  // scorecard method switches (PACE #58/#59): on by default, 0 = legacy behaviour
  'scorecard_shrinkage',
  'scorecard_prior_n',
  'ontime_vs_lane',
  'cold_start_peer_median',
  'cold_start_min_history',
  'logistics_cap',
  'kci_backlog_grace_h',
  'kci_default_delay_h',
])
export const isAdvancedKnob = (k) => ADVANCED_KNOB_KEYS.has(k.key)
